import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Store } from "../type/Store";
import {
  CopyCounts,
  DestinationMenuPreview,
  MenuCopyError,
  MenuCopyPreview,
  copyMenu,
  listStoreMenus,
  loadDestinationMenus,
  loadMenuCopyPreview,
} from "./menuCopy";

type MenuCopyTabProps = {
  stores: Store[];
};

const steps = ["Source", "Review", "Destination", "Copy"];

const countLine = (counts: CopyCounts) =>
  [
    `${counts.menu} menu`,
    `${counts.categories} categories`,
    `${counts.items} items`,
    `${counts.pictures} pictures`,
    `${counts.tags} tags`,
    `${counts.options} advanced options`,
    `${counts.topMenus} top menus`,
    `${counts.combos} combos`,
    `${counts.categoryLimits} category limits`,
  ].join(", ");

const storeLabel = (store?: Store) => store?.name || "Store";

const MenuCopyTab: React.FC<MenuCopyTabProps> = ({ stores }) => {
  const [step, setStep] = useState(0);
  const [sourceId, setSourceId] = useState("");
  const [menuId, setMenuId] = useState("");
  const [menus, setMenus] = useState<Array<{ id: string; name: string }>>([]);
  const [menusLoading, setMenusLoading] = useState(false);
  const [preview, setPreview] = useState<MenuCopyPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [destinationMenus, setDestinationMenus] = useState<DestinationMenuPreview[]>([]);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const [destinationError, setDestinationError] = useState("");
  const [copying, setCopying] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState("");
  const [copyError, setCopyError] = useState("");

  const orderedStores = [...stores].sort((left, right) => storeLabel(left).localeCompare(storeLabel(right)));
  const source = orderedStores.find((store) => store.id === sourceId);
  const destination = orderedStores.find((store) => store.id === destinationId);

  useEffect(() => {
    if (!sourceId) {
      setMenus([]);
      setMenuId("");
      return undefined;
    }
    let cancelled = false;
    setMenusLoading(true);
    setMenuId("");
    setPreview(null);
    listStoreMenus(sourceId)
      .then((rows) => {
        if (!cancelled) setMenus(rows);
      })
      .catch(() => {
        if (!cancelled) setMenus([]);
      })
      .finally(() => {
        if (!cancelled) setMenusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  useEffect(() => {
    if (step !== 1 || !sourceId || !menuId) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError("");
    loadMenuCopyPreview(sourceId, menuId)
      .then((next) => {
        if (!cancelled) setPreview(next);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(error instanceof Error ? error.message : "The menu could not be loaded");
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, sourceId, menuId]);

  useEffect(() => {
    if (!destinationId) {
      setDestinationMenus([]);
      return undefined;
    }
    let cancelled = false;
    setDestinationLoading(true);
    setDestinationError("");
    loadDestinationMenus(destinationId)
      .then((rows) => {
        if (!cancelled) setDestinationMenus(rows);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setDestinationMenus([]);
          setDestinationError(error instanceof Error ? error.message : "The destination could not be loaded");
        }
      })
      .finally(() => {
        if (!cancelled) setDestinationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [destinationId]);

  const resetCopy = () => {
    setStep(0);
    setSourceId("");
    setMenuId("");
    setDestinationId("");
    setPreview(null);
    setResult("");
    setCopyError("");
    setProgress("");
  };

  const runCopy = async () => {
    if (!sourceId || !menuId || !destinationId || copying) return;
    setCopying(true);
    setCopyError("");
    setResult("");
    setProgress("Starting");
    try {
      const counts = await copyMenu({
        sourceStoreId: sourceId,
        menuId,
        destinationStoreId: destinationId,
        onProgress: (update) => setProgress(`${update.label}: ${update.completed} of ${update.total}`),
      });
      setProgress("");
      setResult(`Copied ${countLine(counts)}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Copy failed";
      const written = error instanceof MenuCopyError ? ` Finished before the stop: ${countLine(error.counts)}.` : "";
      setCopyError(`${message}.${written}`);
      setProgress("");
    } finally {
      setCopying(false);
    }
  };

  return (
    <VStack align="stretch" spacing={5}>
      <HStack spacing={2} flexWrap="wrap">
        {steps.map((label, index) => (
          <Box
            key={label}
            px={3}
            py={1}
            borderRadius="md"
            bg={index === step ? "#023047" : "white"}
            color={index === step ? "white" : "gray.600"}
            border="1px solid"
            borderColor={index === step ? "#023047" : "#d5e4ea"}
            fontSize="sm"
            fontWeight="600"
          >
            {index + 1}. {label}
          </Box>
        ))}
      </HStack>

      {step === 0 && (
        <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={5}>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>Source store</FormLabel>
              <Select placeholder="Select a store" value={sourceId} onChange={(event) => setSourceId(event.target.value)} bg="white">
                {orderedStores.map((store) => (
                  <option key={store.id} value={store.id}>{store.name || store.id}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Menu</FormLabel>
              {menusLoading ? <Spinner size="sm" /> : null}
              {!menusLoading && sourceId && menus.length === 0 ? <Text fontSize="sm" color="gray.600">This store has no menus.</Text> : null}
              <VStack align="stretch" spacing={2} mt={menus.length > 0 ? 2 : 0}>
                {menus.map((menu) => (
                  <Button
                    key={menu.id}
                    justifyContent="flex-start"
                    variant={menuId === menu.id ? "solid" : "outline"}
                    colorScheme={menuId === menu.id ? "blue" : undefined}
                    onClick={() => setMenuId(menu.id)}
                  >
                    {menu.name}
                  </Button>
                ))}
              </VStack>
            </FormControl>
          </VStack>
        </Box>
      )}

      {step === 1 && (
        <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={5}>
          {previewLoading ? <Spinner /> : null}
          {previewError ? <Text color="red.600">{previewError}</Text> : null}
          {preview && (
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                {preview.menu.name} from {storeLabel(source)} is copied with its categories, items, pictures, tags, advanced options, top menus, combos, and category limits. Remove anything the destination does not need after the copy.
              </Text>
              <Section title={`Categories (${preview.categories.length})`}>
                {preview.categories.length === 0 ? <Empty /> : preview.categories.map((category) => (
                  <Text key={category.id} fontSize="sm">{category.name} · {category.itemCount} items</Text>
                ))}
              </Section>
              <Section title={`Items (${preview.items.length})`}>
                {preview.items.length === 0 ? <Empty /> : (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {preview.items.map((item) => (
                      <HStack key={item.id} spacing={3} align="center">
                        {item.image ? (
                          <Image src={item.image} alt="" boxSize="40px" objectFit="cover" borderRadius="md" />
                        ) : (
                          <Box boxSize="40px" bg="gray.100" borderRadius="md" />
                        )}
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight="600" noOfLines={1}>{item.name}</Text>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>{item.categoryName}</Text>
                        </Box>
                      </HStack>
                    ))}
                  </SimpleGrid>
                )}
              </Section>
              <NameSection title="Tags" rows={preview.tags} />
              <NameSection title="Advanced options" rows={preview.options} />
              <NameSection title="Top menus" rows={preview.topMenus} />
              <NameSection title="Combos" rows={preview.combos} />
              <NameSection title="Category limits" rows={preview.categoryLimits} />
            </VStack>
          )}
        </Box>
      )}

      {step === 2 && (
        <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={5}>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>Destination store</FormLabel>
              <Select
                placeholder="Select a store"
                value={destinationId}
                onChange={(event) => setDestinationId(event.target.value)}
                bg="white"
              >
                {orderedStores.filter((store) => store.id !== sourceId).map((store) => (
                  <option key={store.id} value={store.id}>{store.name || store.id}</option>
                ))}
              </Select>
            </FormControl>
            {destinationLoading ? <Spinner size="sm" /> : null}
            {destinationError ? <Text color="red.600">{destinationError}</Text> : null}
            {destinationId && !destinationLoading && !destinationError && (
              <Section title={`Menus already on ${storeLabel(destination)}`}>
                {destinationMenus.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">This store has no menus. The copy will be added here.</Text>
                ) : (
                  <>
                    {destinationMenus.map((menu) => (
                      <Text key={menu.id} fontSize="sm">{menu.name} · {menu.categoryCount} categories</Text>
                    ))}
                    <Text fontSize="sm" color="gray.600">The copy is added beside these menus. Nothing already here is replaced.</Text>
                  </>
                )}
              </Section>
            )}
          </VStack>
        </Box>
      )}

      {step === 3 && (
        <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={5}>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm">
              Copy {preview?.menu.name || "this menu"} from {storeLabel(source)} onto {storeLabel(destination)}. The new menu, categories, items, tags, advanced options, and top menus get new IDs and belong to {storeLabel(destination)}.
            </Text>
            {progress ? <Text fontSize="sm" color="gray.600">{progress}</Text> : null}
            {copying ? <Spinner size="sm" /> : null}
            {result ? <Text fontSize="sm" color="green.700">{result}</Text> : null}
            {copyError ? <Text fontSize="sm" color="red.600">{copyError}</Text> : null}
            {!result && (
              <Button colorScheme="blue" h="48px" onClick={runCopy} isDisabled={copying || !destinationId}>
                Copy menu
              </Button>
            )}
            {result && (
              <Button variant="outline" h="48px" onClick={resetCopy}>
                Copy another menu
              </Button>
            )}
          </VStack>
        </Box>
      )}

      <HStack justify="space-between">
        <Button variant="outline" h="44px" onClick={() => setStep((current) => Math.max(0, current - 1))} isDisabled={step === 0 || copying}>
          Back
        </Button>
        {step < 3 && (
          <Button
            colorScheme="blue"
            h="44px"
            onClick={() => setStep((current) => current + 1)}
            isDisabled={
              (step === 0 && !menuId) ||
              (step === 1 && (!preview || previewLoading || Boolean(previewError))) ||
              (step === 2 && (!destinationId || destinationLoading || Boolean(destinationError)))
            }
          >
            Next
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Text fontSize="sm" fontWeight="700" mb={2}>{title}</Text>
    <VStack align="stretch" spacing={1}>{children}</VStack>
  </Box>
);

const NameSection: React.FC<{ title: string; rows: Array<{ id: string; name: string }> }> = ({ title, rows }) => (
  <Section title={`${title} (${rows.length})`}>
    {rows.length === 0 ? <Empty /> : rows.map((row) => (
      <Text key={row.id} fontSize="sm">{row.name}</Text>
    ))}
  </Section>
);

const Empty = () => <Text fontSize="sm" color="gray.500">None</Text>;

export default MenuCopyTab;
